import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRightLeft, Eye } from "lucide-react";
import type { WidgetAction } from "@/lib/mechanics/types";
import { useLocale } from "@/hooks/useLocale";

interface POVCharacter {
  name: string;
  id?: string;
  location?: string;
  status?: string;
  last_active_turn?: number | null;
}

interface POVPanelProps {
  activePov?: string | null;
  povCharacters?: POVCharacter[];
  povHistory?: any[];
  cooldownUntil?: number;
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
}

export function POVPanel({ activePov, povCharacters, povHistory, widgetActions, onWidgetAction }: POVPanelProps) {
  const { t } = useLocale();
  const chars = povCharacters || [];

  if (chars.length === 0) {
    return (
      <div className="p-3 text-sm text-muted-foreground">
        <Eye className="inline-block w-4 h-4 mr-1.5" />
        {t.play_no_pov}
      </div>
    );
  }

  const switchAction = widgetActions?.[0];

  return (
    <div className="space-y-2 p-3">
      {chars.map((c) => {
        const isActive = c.name === activePov || c.id === activePov;
        return (
          <div
            key={c.name || c.id}
            className={`flex items-center justify-between p-2 rounded-md border ${
              isActive ? "border-primary bg-primary/10" : "border-border"
            }`}
          >
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium">{c.name}</span>
                {isActive && (
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">
                    {t.play_active}
                  </Badge>
                )}
              </div>
              {c.location && (
                <span className="text-xs text-muted-foreground">📍 {c.location}</span>
              )}
            </div>
            {!isActive && switchAction && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => onWidgetAction?.(switchAction, { target_character: c.name || c.id || "" })}
              >
                <ArrowRightLeft className="w-3 h-3 mr-1" />
                {t.play_switch}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
